import React, {useState} from "react";
import HeaderLesson from "./HeaderLesson";
import FooterLesson from "./FooterLesson";
import LessonScreen from "./LessonScreen";
import LessonComplete from "./LessonComplete";
import StreakComplete from "./StreakComplete";
import ContentMessage from "./ContentMessage";
import Button from "../../../../components/elements/Button";
import useLessonProgress from "./hooks/useLessonProgress";
import useLessonSession from "./hooks/useLessonSession";

/**
 * Renders a lesson and its ending:
 * lesson screens -> lesson complete -> (streak screen, if the streak increased)
 */
const LessonView = ({
                        content,
                        userId,
                        courseId,
                        contentId,
                        lessonAttemptId,
                        alreadyCompleted = false,
                        onExit,
                    }) => {
    const {
        currentScreen,
        currentCompletedBlocks,
        currentScreenIndex,
        isScreenComplete,
        isLastScreen,
        isFinished,
        progress,
        handleBlockComplete,
        goNext,
        goPrevious,
    } = useLessonProgress(content?.fragments);

    const {
        recordAnswer,
        recordHint,
        completeLesson,
        completing,
        completeError,
        outOfEnergy,
        result,
    } = useLessonSession({
        userId,
        courseId,
        contentId,
        lessonAttemptId,
        alreadyCompleted,
    });

    const [showStreak, setShowStreak] = useState(false);

    /** On the last screen, save the result first, then open the completion screen. */
    const handleNext = async () => {
        if (isLastScreen) {
            const {success} = await completeLesson();
            if (!success) return; // error is shown above the footer, learner can retry
        }

        goNext();
    };

    /** After "lesson complete": show the streak screen when it just increased. */
    const handleCompleteContinue = () => {
        if (result?.streakChanged) {
            setShowStreak(true);
            return;
        }

        onExit();
    };

    if (!currentScreen) {
        return <ContentMessage message="محتوایی برای نمایش وجود ندارد."/>;
    }

    if (outOfEnergy) {
        return (
            <ContentMessage
                message="انرژی‌ات تمام شد. بعد از شارژ شدن دوباره تلاش کن."
                footer={
                    <div className="flex justify-center py-5">
                        <Button onClick={onExit}>بازگشت</Button>
                    </div>
                }
            />
        );
    }

    if (isFinished && showStreak) {
        return <StreakComplete streak={result?.streak} onContinue={onExit}/>;
    }

    if (isFinished) {
        return (
            <LessonComplete
                credit={result?.credit}
                xp={result?.xp}
                showRewards={!alreadyCompleted}
                onContinue={handleCompleteContinue}
            />
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <HeaderLesson progress={progress}/>

            <main className="flex-1 w-full">
                <LessonScreen
                    screen={currentScreen}
                    completedBlocks={currentCompletedBlocks}
                    onBlockComplete={handleBlockComplete}
                    onAnswer={recordAnswer}
                    onHint={recordHint}
                />
            </main>

            {completeError && (
                <p className="py-2 text-center text-red-500">{completeError}</p>
            )}

            <FooterLesson
                goPrevious={goPrevious}
                goNext={handleNext}
                hasPrevious={currentScreenIndex > 0}
                hasNext={isScreenComplete && !completing}
                isLastScreen={isLastScreen}
            />
        </div>
    );
};

export default LessonView;